import { ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// models
import { ChartType } from './chart-type.model';
import { DecimalPipe } from '@angular/common';
import { ChartComponent } from 'ng-apexcharts';

@Component({
    selector: 'app-dashboard-chart',
    templateUrl: './dashboardChart.component.html',
    styleUrls: ['./dashboardChart.component.scss'],
    standalone: false
})
export class DashboardChartComponent implements OnInit {

  // Add ViewChild to reference the chart
  @ViewChildren('chart') charts: QueryList<ChartComponent>;

  @Input() dashboardChartData: any[];
  @Input() chartColors: string[] = [];
  @Input() colWidth: number
  @Input() chartWidth: string;
  @Input() showAsInteger: boolean = false;

  chartData: ChartType[] = [];
  private originalChartTypes: string[] = [];


  private chartStates: Map<number, {
    isTransitioning: boolean;
    lastType: string;
    recreationCount: number;
  }> = new Map();

  // Add available chart types
  availableChartTypes = [
    {
      value: 'column',
      label: 'Column Chart',
      icon: 'ri-bar-chart-line'
    },
    {
      value: 'bar',
      label: 'Bar Chart',
      icon: 'ri-bar-chart-2-line'
    },
    {
      value: 'area',
      label: 'Area Chart',
      icon: 'ri-line-chart-line'
    }
  ];

  constructor(private _decimalPipe: DecimalPipe, public _translate: TranslateService, private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.cashPositioningChart()
  }

  getAvailableChartTypesForIndex(chartIndex: number): any[] {
    const chart = this.dashboardChartData[chartIndex];

    if (!chart || !chart.dataSeries) {
      return this.availableChartTypes;
    }

    // ★ SIMPLE FILTERING: Show area only if data supports it
    const hasMultipleCategories = chart.labelsEn && chart.labelsEn.length > 1;
    const hasMultipleDataPoints = chart.dataSeries.some((series: any) =>
      series.data && series.data.length > 1
    );

    // FILTER THE EXISTING ARRAY instead of creating new objects
    let filteredTypes = this.availableChartTypes.filter(chartType => {
      if (chartType.value === 'area') {
        // Only include area if data supports it
        return hasMultipleCategories && hasMultipleDataPoints;
      }
      // Always include column and bar
      return chartType.value === 'column' || chartType.value === 'bar';
    });

    return filteredTypes;
  }

  private getUnifiedChartConfiguration(): any {
    return {
      // UNIFIED RESPONSIVE CONFIGURATION
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300,
              toolbar: {
                show: false
              }
            },
            plotOptions: {
              bar: {
                columnWidth: '85%'  // ← Consistent responsive width
              }
            },
            legend: {
              position: 'bottom'
            }
          }
        }
      ],

      // UNIFIED CHART BASE SETTINGS
      chart: {
        height: 400,
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          resetIcon: {
            offsetX: -10,
            offsetY: 0,
            fillColor: '#fff',
            strokeColor: '#37474F',
            size: 13,
            position: 'bottom'
          }
        },
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        },
        selection: {
          enabled: true,
          type: 'x',
          fill: {
            color: '#24292e',
            opacity: 0.1
          },
          stroke: {
            width: 1,
            color: '#24292e',
            opacity: 0.4,
            dashArray: 3
          }
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },

      // UNIFIED GRID CONFIGURATION
      grid: {
        show: true,
        borderColor: '#e9ecef',
        strokeDashArray: 2,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: 10,
          right: 15,
          bottom: 15,
          left: 15,
        },
      },

      // UNIFIED LEGEND CONFIGURATION
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '13px',
        fontFamily: 'Segoe UI, Arial',
        fontWeight: 500,
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        },
        markers: {
          width: 14,
          height: 14,
          radius: 4
        },
        labels: {
          useSeriesColors: false,
          colors: '#495057'
        },
        itemMargin: {
          horizontal: 20,
          vertical: 8,
        },
      }
    };
  }

  /**
   * STEP 2: Create chart type specific plotOptions that maintain consistency
   */
  private getPlotOptionsForType(chartType: string): any {

    // ★ CRITICAL: Check if this is a stacked chart from backend data
    const isStackedChart = chartType === 'STACKED_BAR_CHART' ||
      this.isChartStacked(chartType); // You'll need this helper

    switch (chartType) {
      case 'column':
        return {
          bar: {
            horizontal: false,
            borderRadiusWhenStacked: 'last',
            columnWidth: '65%',
            distributed: false,
            colors: {
              ranges: [],
              backgroundBarColors: [],
              backgroundBarOpacity: 1
            },
            dataLabels: {
              position: 'top'
            }
          }
        };

      case 'bar':
        return {
          bar: {
            horizontal: true,
            borderRadiusWhenStacked: 'last',
            columnWidth: '65%',
            distributed: false,
            colors: {
              ranges: [],
              backgroundBarColors: [],
              backgroundBarOpacity: 1
            },
            dataLabels: {
              position: 'center'
            }
          }
        };

      // ★ NEW: Handle STACKED_BAR_CHART specifically
      case 'STACKED_BAR_CHART':
        return {
          bar: {
            horizontal: false, // Keep as column (you can change to true for horizontal)
            borderRadiusWhenStacked: 'last',
            // ★ WIDER COLUMNS FOR STACKED CHARTS
            columnWidth: '75%', // ← Wider than normal charts
            distributed: false,
            colors: {
              ranges: [],
              backgroundBarColors: [],
              backgroundBarOpacity: 1
            },
            dataLabels: {
              position: 'center' // ← Better for stacked
            }
          }
        };

      case 'area':
        return {
          area: {
            fillTo: 'origin',
            distributed: false
          }
        };

      case 'line':
        return {
          line: {
            distributed: false
          }
        };

      default:
        return this.getPlotOptionsForType('column');
    }
  }

  private isChartStacked(chartType: string): boolean {
    // Check if the chart should be stacked based on backend type
    return chartType === 'STACKED_BAR_CHART' ||
      chartType === 'STACKED_COLUMN_CHART';
  }

  /**
   * STEP 3: Create consistent styling for each chart type
   */
  private getStrokeConfigForType(chartType: string, colors: string[]): any {
    switch (chartType) {
      case 'area':
      case 'line':
        return {
          width: 3,
          curve: 'smooth',
          colors: colors
        };

      case 'column':
      case 'bar':
      default:
        return {
          width: 1,
          colors: ['#ffffff']
        };
    }
  }

  private getFillConfigForType(chartType: string, colors: string[]): any {
    switch (chartType) {
      case 'area':
        return {
          type: 'gradient',
          colors: colors,
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.5,
            gradientToColors: colors.map(color => color + '80'),
            inverseColors: false,
            opacityFrom: 0.7,
            opacityTo: 0.3,
            stops: [0, 50, 100]
          }
        };

      case 'line':
        return {
          type: 'solid',
          colors: colors,
          opacity: 1
        };

      case 'column':
      case 'bar':
      default:
        return {
          type: 'solid',
          colors: colors,
          opacity: 0.9,
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.1,
            gradientToColors: undefined,
            inverseColors: false,
            opacityFrom: 0.9,
            opacityTo: 0.75,
            stops: [0, 90, 100]
          }
        };
    }
  }

  private getMarkersConfigForType(chartType: string, colors: string[]): any {
    switch (chartType) {
      case 'area':
      case 'line':
        return {
          size: 4,
          colors: colors,
          strokeColors: colors,
          distributed: false,
          hover: { size: 7 }
        };

      case 'column':
      case 'bar':
      default:
        return {
          size: 0,
          distributed: false
        };
    }
  }

  // Updated cashPositioningChart method
  private cashPositioningChart() {
    this.chartData = [];
    this.originalChartTypes = [];

    this.dashboardChartData.forEach((chart, chartIndex) => {
      let chartTypeValue = chart.chartType;
      let series: any = [];
      let Unit = '';
      // Get the correct frontend chart type
      this.originalChartTypes[chartIndex] = this.mapBackendToFrontend(chartTypeValue);

      if (chart.dataSeries) {
        Unit = chart.dataSeries[0].unitEn;
        let defaultColors = [
          '#2E7DFB', '#FF6B6B', '#4CAF50', '#FF71C4', '#FF9F1C',
          '#00BCD4', '#98CE00', '#FF5252', '#45B7D1', '#9D4EDD',
        ];

        chart.dataSeries.forEach((data: any, index: number) => {
          let seriesName = this._translate.currentLang === 'en' ? data.nameEn : data.nameAr;
          if (!seriesName) {
            seriesName = `Series ${index + 1}`;
          }

          let legendColor: string;
          if (this.chartColors && this.chartColors.length) {
            legendColor = this.chartColors[index % this.chartColors.length];
          } else {
            if (data.nameEn === 'Inflow') {
              legendColor = '#4CAF50';
            } else if (data.nameEn === 'Outflow') {
              legendColor = '#DC2626';
            } else {
              legendColor = defaultColors[index % defaultColors.length];
            }
          }

          let dataSet: any = {
            name: seriesName,
            data: data.data,
            legendColor: legendColor
          }
          series.push(dataSet);
        });
      }

      let chartConfig = this.getChartConfiguration(chartTypeValue);
      const seriesColors = series.map((s: any) => s.legendColor);
      const formattedLabels = chart.labelsEn ? this.formatWeekLabels(chart.labelsEn) : [];
      let _self = this;

      const baseConfig = this.getUnifiedChartConfiguration();
      const currentChartType = this.originalChartTypes[chartIndex];

      // ★ FIX: Calculate plotOptions ONCE with all considerations
      let plotOptions;

      if (chartTypeValue === 'STACKED_BAR_CHART') {
        plotOptions = {
          bar: {
            horizontal: true,  // ← FIX: Horizontal for stacked BARS
            borderRadiusWhenStacked: 'last',
            columnWidth: '70%', // ← FIX: Better width (not 20%!)
            distributed: false,
            colors: {
              ranges: [],
              backgroundBarColors: [],
              backgroundBarOpacity: 1
            },
            dataLabels: {
              position: 'center'
            }
          }
        };
      } else if (chartTypeValue === 'STACKED_COLUMN_CHART') {
        plotOptions = {
          bar: {
            horizontal: false, // ← Vertical for stacked COLUMNS
            borderRadiusWhenStacked: 'last',
            columnWidth: '70%',
            distributed: false,
            colors: {
              ranges: [],
              backgroundBarColors: [],
              backgroundBarOpacity: 1
            },
            dataLabels: {
              position: 'center'
            }
          }
        };
      } else {
        // Use normal chart type logic
        plotOptions = this.getPlotOptionsForType(currentChartType);
      }
      // uild the chart configuration
      this.chartData.push({
        series: series,
        labels: formattedLabels,
        colors: seriesColors,
        _currentChartType: currentChartType,

        chart: {
          ...baseConfig.chart,
          type: (currentChartType === 'column' || currentChartType === 'bar') ? 'bar' : currentChartType,
          stacked: chartConfig.isStacked // ← CRITICAL: This enables stacking
        },

        // ★ USE THE CALCULATED PLOT OPTIONS (NO OVERRIDE)
        plotOptions: plotOptions,

        stroke: this.getStrokeConfigForType(currentChartType, seriesColors),
        fill: this.getFillConfigForType(currentChartType, seriesColors),
        markers: this.getMarkersConfigForType(currentChartType, seriesColors),
        responsive: baseConfig.responsive,
        grid: baseConfig.grid,
        legend: {
          ...baseConfig.legend,
          markers: {
            ...baseConfig.legend.markers,
            fillColors: seriesColors
          }
        },

        // ... rest of your configuration (tooltip, yaxis, xaxis, dataLabels)
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          followCursor: true,
          fillSeriesColor: false,
          theme: 'light',
          style: {
            fontSize: '13px',
            fontFamily: 'Segoe UI, Arial, sans-serif',
          },
          onDatasetHover: {
            highlightDataSeries: true,
          },
          custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
            const categoryName = w.globals.labels[dataPointIndex];
            let tooltipContent = `
            <div class="custom-tooltip" style="
              background: white; 
              padding: 15px; 
              border-radius: 8px; 
              box-shadow: 0 4px 20px rgba(0,0,0,0.15);
              border: 1px solid #e9ecef;
              min-width: 200px;
            ">
              <div style="
                font-weight: 600; 
                margin-bottom: 10px; 
                color: #495057;
                border-bottom: 1px solid #e9ecef;
                padding-bottom: 8px;
              ">${categoryName}</div>
          `;

            series.forEach((seriesData: any, index: number) => {
              const seriesName = w.globals.seriesNames[index];
              const value = seriesData[dataPointIndex];

              let formattedValue;
              if (typeof value === 'number' && !isNaN(value)) {
                formattedValue = _self.showAsInteger ?
                  _self._decimalPipe.transform(value, '1.0-0') :
                  _self._decimalPipe.transform(value, '1.2-2');
              } else {
                formattedValue = value;
              }

              tooltipContent += `
              <div style="
                display: flex; 
                align-items: center; 
                margin-bottom: 8px;
                padding: 4px 0;
              ">
                <div style="
                  width: 12px; 
                  height: 12px; 
                  background: ${seriesColors[index]}; 
                  border-radius: 3px; 
                  margin-right: 10px;
                "></div>
                <div style="flex-grow: 1;">
                  <span style="font-weight: 500; color: #495057;">${seriesName}:</span>
                  <span style="
                    font-weight: 600; 
                    color: ${seriesColors[index]};
                    margin-left: 8px;
                  ">${formattedValue} ${Unit}</span>
                </div>
              </div>
            `;
            });

            if (series.length === 2) {
              const inflowValue = series[0][dataPointIndex];
              const outflowValue = series[1][dataPointIndex];

              if (typeof inflowValue === 'number' && typeof outflowValue === 'number' &&
                !isNaN(inflowValue) && !isNaN(outflowValue)) {
                const netFlow = inflowValue - outflowValue;
                const formattedNetFlow = _self.showAsInteger ?
                  _self._decimalPipe.transform(netFlow, '1.0-0') :
                  _self._decimalPipe.transform(netFlow, '1.2-2');

                tooltipContent += `
          <div style="
            border-top: 1px solid #e9ecef; 
            margin-top: 8px; 
            padding-top: 8px;
          ">
            <div style="
              display: flex; 
              align-items: center; 
              font-weight: 600;
            ">
              <div style="
                width: 12px; 
                height: 12px; 
                background: ${netFlow >= 0 ? '#28a745' : '#dc3545'}; 
                border-radius: 3px; 
                margin-right: 10px;
              "></div>
              <span style="color: #495057;">Net Flow:</span>
              <span style="
                color: ${netFlow >= 0 ? '#28a745' : '#dc3545'};
                margin-left: 8px;
              ">${formattedNetFlow} ${Unit}</span>
            </div>
          </div>
        `;
              }
            }

            tooltipContent += '</div>';
            return tooltipContent;
          }
        },

        yaxis: {
          labels: {
            formatter: function (value: any) {
              if (typeof value !== 'number' || isNaN(value)) {
                return value;
              }

              return _self.showAsInteger ?
                _self._decimalPipe.transform(value, '1.0-0') + ' ' + Unit :
                _self._decimalPipe.transform(value, '1.2-2') + ' ' + Unit;
            },
            style: {
              colors: '#666',
              fontSize: '11px'
            }
          },
          title: {
            text: `Amount (${Unit})`,
            style: {
              color: '#666',
              fontSize: '12px',
              fontWeight: 600
            }
          }
        },

        xaxis: {
          title: {
            text: 'Bank Accounts',
            style: {
              color: '#666',
              fontSize: '12px',
              fontWeight: 600
            }
          },
          labels: {
            rotate: -45,
            rotateAlways: false,
            trim: true,
            maxHeight: 120,
            style: {
              fontSize: '11px',
              colors: '#666'
            }
          }
        },

        dataLabels: {
          enabled: false,
          enabledOnSeries: undefined,
          formatter: function (value: any) {
            if (typeof value !== 'number' || isNaN(value)) {
              return value;
            }

            return _self.showAsInteger ?
              _self._decimalPipe.transform(value, '1.0-0') + ' ' + Unit :
              _self._decimalPipe.transform(value, '1.2-2') + ' ' + Unit;
          },
          style: {
            fontSize: '11px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
            fontWeight: '600',
            colors: ['#1f2937']
          },
          offsetX: 0,
          offsetY: -8,
          background: {
            enabled: true,
            foreColor: '#ffffff',
            borderRadius: 6,
            padding: 6,
            opacity: 0.95,
            borderWidth: 1,
            borderColor: '#e5e7eb',
            dropShadow: {
              enabled: true,
              top: 1,
              left: 1,
              blur: 3,
              color: 'rgba(0, 0, 0, 0.1)',
              opacity: 0.8
            }
          },
          textAnchor: 'middle',
          distributed: false,
        }
      });
    });
  }
  // New helper method to map backend chart types to frontend types
  private mapBackendToFrontend(backendType: string): string {
    const mapping: { [key: string]: string } = {
      'COLUMN_CHART': 'column',
      'BAR_CHART': 'bar',
      'STACKED_BAR_CHART': 'bar', // ← Map to column but keep stacked=true
      'STACKED_COLUMN_CHART': 'column',
      'LINE_CHART': 'line',
      'AREA_CHART': 'area',
      'PIE_CHART': 'pie'
    };
    return mapping[backendType] || 'column';
  }

  // Helper method to determine chart configuration
  private getChartConfiguration(chartType: string) {
    switch (chartType) {
      case 'BAR_CHART':
        return {
          isHorizontal: true,
          isStacked: false,
          description: 'Horizontal bars for easy reading of long labels'
        };

      case 'COLUMN_CHART':
        return {
          isHorizontal: false,
          isStacked: false,
          description: 'Vertical columns for comparing values'
        };

      case 'STACKED_BAR_CHART':
        return {
          isHorizontal: true,  // ← FIX: TRUE for horizontal bars!
          isStacked: true,
          description: 'Horizontal stacked bars showing parts of a whole'
        };

      case 'STACKED_COLUMN_CHART':
        return {
          isHorizontal: false, // ← FALSE for vertical columns
          isStacked: true,
          description: 'Vertical stacked columns showing parts of a whole'
        };

      default:
        return {
          isHorizontal: false,
          isStacked: false,
          description: 'Default column chart'
        };
    }
  }


  //   1: Enhanced Chart Type Switching for specific chart
  changeChartType(chartIndex: number, newType: string): void {
    if (!this.chartData[chartIndex]) {
      console.warn(`Chart data not found for index ${chartIndex}`);
      return;
    }

    const chartComponent = this.charts.toArray()[chartIndex];
    if (!chartComponent) {
      console.warn(`Chart component not found for index ${chartIndex}`);
      return;
    }

    const currentType = this.getCurrentChartType(chartIndex);
    if (currentType === newType) {
      return;
    }

    // 🆕 SIMPLE APPROACH: Use data refresh for problematic transitions
    const isComingFromArea = (currentType === 'area');
    const isGoingToBar = (newType === 'bar' || newType === 'column');

    if (isComingFromArea && isGoingToBar) {
      this.refreshChartData(chartIndex, newType);
    } else {
      this.performNormalChartUpdate(chartIndex, newType);
    }
  }

  private refreshChartData(chartIndex: number, newType: string): void {

    try {
      // 🔹 STEP 1: Get current data
      const currentData = this.chartData[chartIndex];
      const originalBackendType = this.dashboardChartData[chartIndex]?.chartType;
      const isStackedChart = originalBackendType === 'STACKED_BAR_CHART' || originalBackendType === 'STACKED_COLUMN_CHART';
      const apexChartType = (newType === 'column' || newType === 'bar') ? 'bar' : newType;

      // 🔹 STEP 2: Build fresh chart configuration
      const baseConfig = this.getUnifiedChartConfiguration();

      // 🔹 STEP 3: Create completely new chart object
      const freshChart = {
        chart: {
          ...baseConfig.chart,
          type: apexChartType,
          stacked: isStackedChart,
          id: `refresh-${chartIndex}-${newType}-${Date.now()}` // Force fresh render
        },

        // Preserve data but refresh everything else
        series: currentData.series,
        labels: currentData.labels,
        colors: currentData.colors,
        _currentChartType: newType,

        // Fresh configurations
        plotOptions: this.getFreshPlotOptionsForType(newType, isStackedChart),
        stroke: this.getStrokeConfigForType(newType, currentData.colors),
        fill: this.getFillConfigForType(newType, currentData.colors),
        markers: this.getMarkersConfigForType(newType, currentData.colors),

        // Preserve functional parts
        tooltip: currentData.tooltip,
        xaxis: currentData.xaxis,
        yaxis: currentData.yaxis,
        dataLabels: {
          ...currentData.dataLabels,
          enabled: false // Reset to prevent conflicts
        },
        legend: {
          ...baseConfig.legend,
          markers: {
            ...baseConfig.legend.markers,
            fillColors: currentData.colors
          }
        },
        grid: baseConfig.grid,
        responsive: baseConfig.responsive
      };

      // 🔹 STEP 4: Apply the change by replacing the entire chart data
      this.chartData[chartIndex] = freshChart;

      // 🔹 STEP 5: Trigger Angular change detection
      this.cdr.detectChanges();

    } catch (error) {
      // Fallback to normal update
      this.performNormalChartUpdate(chartIndex, newType);
    }
  }

  // 🆕 NEW METHOD: Get fresh plotOptions specifically designed to avoid ApexCharts bug
  private getFreshPlotOptionsForType(chartType: string, isStacked: boolean = false): any {
    // 🔹 Don't use undefined - use proper empty objects or omit properties entirely
    switch (chartType) {
      case 'column':
        return {
          bar: {
            horizontal: false,
            borderRadiusWhenStacked: isStacked ? 'last' : undefined,
            columnWidth: isStacked ? '75%' : '65%',
            distributed: false, // ← Always provide this property
            colors: {
              ranges: [],
              backgroundBarColors: [],
              backgroundBarOpacity: 1
            },
            dataLabels: {
              position: 'top'
            }
          }
        };

      case 'bar':
        return {
          bar: {
            horizontal: true,
            borderRadiusWhenStacked: isStacked ? 'last' : undefined,
            columnWidth: isStacked ? '75%' : '65%',
            distributed: false, // ← Always provide this property
            colors: {
              ranges: [],
              backgroundBarColors: [],
              backgroundBarOpacity: 1
            },
            dataLabels: {
              position: 'center'
            }
          }
        };

      case 'area':
        return {
          area: {
            fillTo: 'origin',
            distributed: false // ← Always provide this property
          }
        };

      case 'line':
        return {
          line: {
            distributed: false // ← Always provide this property
          }
        };

      default:
        return {
          bar: {
            horizontal: false,
            columnWidth: '65%',
            distributed: false // ← Always provide this property
          }
        };
    }
  }

  // 🆕 NEW METHOD: Normal update for non-problematic transitions
  private performNormalChartUpdate(chartIndex: number, newType: string): void {
    const chartComponent = this.charts.toArray()[chartIndex];
    const originalColors = this.chartData[chartIndex].colors || [];
    const validColors = originalColors.length > 0 ? [...originalColors] : ['#4CAF50', '#DC2626', '#2E7DFB', '#FF6B6B'];
    const apexChartType = (newType === 'column' || newType === 'bar') ? 'bar' : newType;

    // 🔹 Get current configuration
    const originalBackendType = this.dashboardChartData[chartIndex]?.chartType;
    const isStackedChart = originalBackendType === 'STACKED_BAR_CHART' || originalBackendType === 'STACKED_COLUMN_CHART';
    const baseConfig = this.getUnifiedChartConfiguration();

    // 🔹 Build complete configuration for normal update
    const completeConfig: any = {
      chart: {
        ...baseConfig.chart,
        type: apexChartType,
        stacked: isStackedChart
      },
      colors: validColors,
      _currentChartType: newType,
      series: this.chartData[chartIndex].series,
      labels: this.chartData[chartIndex].labels,

      // 🔹 Use fresh plotOptions
      plotOptions: this.getFreshPlotOptionsForType(newType, isStackedChart),
      stroke: this.getStrokeConfigForType(newType, validColors),
      fill: this.getFillConfigForType(newType, validColors),
      markers: this.getMarkersConfigForType(newType, validColors),
      responsive: baseConfig.responsive,

      dataLabels: {
        enabled: this.chartData[chartIndex].dataLabels?.enabled || false,
        distributed: false,
        formatter: this.chartData[chartIndex].dataLabels?.formatter,
        style: this.chartData[chartIndex].dataLabels?.style || {
          fontSize: '11px',
          fontWeight: '600',
          colors: ['#1f2937']
        },
        textAnchor: 'middle',
        offsetX: 0,
        offsetY: -8,
        background: this.chartData[chartIndex].dataLabels?.background
      },
      tooltip: this.chartData[chartIndex].tooltip,
      legend: {
        ...baseConfig.legend,
        markers: {
          ...baseConfig.legend.markers,
          fillColors: validColors
        }
      },
      grid: baseConfig.grid,
      xaxis: this.chartData[chartIndex].xaxis,
      yaxis: this.chartData[chartIndex].yaxis
    };

    try {
      // 🔹 Attempt normal update
      this.chartData[chartIndex] = completeConfig;
      chartComponent.updateOptions(completeConfig, true, true, true);
    } catch (error) {
      // 🔹 Fallback to recreation on any error
      this.refreshChartData(chartIndex, newType);
    }
  }

  // ADDITIONAL: Safe data labels toggle
  // toggleDataLabels(chartIndex: number): void {
  //   if (!this.chartData[chartIndex]) return;

  //   const currentEnabled = this.chartData[chartIndex].dataLabels?.enabled || false;

  //   // Build complete dataLabels config (never partial updates)
  //   const completeDataLabels = {
  //     enabled: !currentEnabled,
  //     distributed: false, // ← Always include
  //     formatter: this.chartData[chartIndex].dataLabels?.formatter,
  //     style: this.chartData[chartIndex].dataLabels?.style || {
  //       fontSize: '11px',
  //       fontWeight: '600',
  //       colors: ['#1f2937']
  //     },
  //     textAnchor: 'middle',
  //     offsetX: 0,
  //     offsetY: -8,
  //     background: this.chartData[chartIndex].dataLabels?.background
  //   };

  //   // Update local data
  //   this.chartData[chartIndex].dataLabels = completeDataLabels;

  //   // Update chart
  //   const chartComponent = this.charts.toArray()[chartIndex];
  //   if (chartComponent) {
  //     try {
  //       chartComponent.updateOptions({
  //         dataLabels: completeDataLabels
  //       }, false, true);
  //     } catch (error) {
  //     }
  //   }
  // }

  toggleDataLabels(chartIndex: number): void {
    const chart = this.chartData[chartIndex];
    if (chart && chart.dataLabels) {
      chart.dataLabels.enabled = !chart.dataLabels.enabled;

      const chartComponent = this.charts.toArray()[chartIndex];
      if (chartComponent) {
        chartComponent.updateOptions({
          dataLabels: chart.dataLabels
        }, false, true);
      }
    }
  }

  // Helper methods
  isDataLabelsEnabled(chartIndex: number): boolean {
    return this.chartData[chartIndex]?.dataLabels?.enabled || false;
  }

  getCurrentChartType(chartIndex: number): string {
    // Return the stored frontend chart type, not the ApexCharts internal type
    return this.chartData[chartIndex]?._currentChartType || 'column';
  }

  /**
   * Formats week labels to be more concise and readable while including year information
   * @param labels Original week labels array
   * @returns Formatted labels array
   */
  private formatWeekLabels(labels: string[]): string[] {
    return labels.map(label => {
      // Check if it's a week label format
      if (label.startsWith('Week')) {
        // Extract dates from format "Week DD/MM/YYYY-DD/MM/YYYY"
        const dateRange = label.replace('Week ', '');
        const dates = dateRange.split('-');

        if (dates.length === 2) {
          // Extract start and end dates
          const startParts = dates[0].split('/');
          const endParts = dates[1].split('/');

          if (startParts.length === 3 && endParts.length === 3) {
            // Get start day, month and year
            const startDay = startParts[0];
            const startMonth = this.getShortMonthName(parseInt(startParts[1]));
            const startYear = startParts[2];

            // Get end day, month and year
            const endDay = endParts[0];
            const endMonth = this.getShortMonthName(parseInt(endParts[1]));
            const endYear = endParts[2];

            // Format with year included: "DD MMM YY - DD MMM YY"
            // Use last 2 digits of year for compactness
            const startYearShort = startYear.substring(2);
            const endYearShort = endYear.substring(2);

            // If years are different, include both years in the label
            if (startYear !== endYear) {
              return `${startDay} ${startMonth} '${startYearShort} - ${endDay} ${endMonth} '${endYearShort}`;
            }
            // If years are the same, only include year once at the end
            else {
              return `${startDay} ${startMonth} - ${endDay} ${endMonth} '${startYearShort}`;
            }

            // Alternative formatting options:

            // Option 1: "DD MMM - DD MMM" (e.g., "08 Dec - 14 Dec")
            // return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;

            // Option 2: Shortened even more if same month "DD-DD MMM" (e.g., "08-14 Dec")
            // if (startMonth === endMonth) {
            //   return `${startDay}-${endDay} ${startMonth}`;
            // } else {
            //   return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
            // }

            // Option 3: Just show start date "DD MMM" (e.g., "08 Dec")
            // return `${startDay} ${startMonth}`;
          }
        }
      }
      // Return original label if it doesn't match expected format
      return label;
    });
  }


  /**
   * Converts month number to short month name
   * @param monthNum Month number (1-12)
   * @returns Short month name
   */
  private getShortMonthName(monthNum: number): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1] || '';
  }


}
